/**
 * One turn in the audit log.
 *
 * Collapsed it says what happened. Expanded it shows both halves of the platform's
 * job side by side: what it decided and told the agent, and what it did with what
 * the agent wrote back.
 */
import type {
  SessionGuardrailResult,
  SessionGuardrailTree,
} from "@financial-advisory/data-access/api-types";
import { UnlikelyMark } from "@targeted-support/ui/features/overview";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  ShieldCheck,
  X,
} from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";
import type { ChangedNode } from "@unlikelyai-magic/utils";
import { MarkdownContent } from "../MarkdownContent";
import { guardrailPassed } from "../guardrailUtils";
import { buildBriefing, contradictionSentence } from "../briefing";
import {
  type AuditTurn,
  checksRunOnTurn,
  draftFailed,
  draftsForTurn,
  formatDuration,
  outcomesRuledOutOnTurn,
} from "./auditTurns";
import type { ConversationPair } from "../types";

const failedTrees = (draft: SessionGuardrailResult): SessionGuardrailTree[] =>
  (draft.guardrail_trees ?? []).filter((tree) => !guardrailPassed(tree));

/**
 * Why a draft failed, in the platform's own words: the rule it broke and the
 * reasoning recorded against the criterion that decided it.
 */
const failureReason = (
  draft: SessionGuardrailResult
): {
  rule: string;
  criterion?: string;
  value?: string;
  reasoning?: string;
} | null => {
  const failed = failedTrees(draft);
  if (failed.length === 0) return null;
  const tree = failed[0];
  const leaf = (tree.leaf_nodes ?? []).find((node) => node.reasoning);
  return {
    rule: tree.guardrail_tree.name,
    criterion: leaf?.name,
    value: leaf?.value ?? undefined,
    reasoning: leaf?.reasoning,
  };
};

const Shield = ({ failed }: { failed: boolean }) => {
  const Icon = failed ? ShieldAlert : ShieldCheck;
  return (
    <Icon
      className={cn(
        "h-[15px] w-[15px] flex-none",
        failed ? "text-red-500" : "text-green-600"
      )}
      fill="currentColor"
      stroke="white"
      aria-hidden={true}
    />
  );
};

const ShieldSequence = ({ drafts }: { drafts: SessionGuardrailResult[] }) => (
  <span className="flex items-center gap-1.5">
    {drafts.map((draft, index) => (
      <span key={draft.id} className="flex items-center gap-1.5">
        <Shield failed={draftFailed(draft)} />
        {index < drafts.length - 1 && (
          <span className="text-[10px] leading-none text-brand-muted">
            &bull;&bull;&bull;
          </span>
        )}
      </span>
    ))}
  </span>
);

const BlockLabel = ({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "amber" | "green" | "red";
}) => {
  const colour = {
    muted: "text-brand-muted",
    amber: "text-amber-700",
    green: "text-green-600",
    red: "text-red-600",
  }[tone];
  return (
    <h4
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] ${colour}`}
    >
      {children}
    </h4>
  );
};

const Draft = ({
  draft,
  index,
  isDelivered,
  pickedUpTargetQuestion,
}: {
  draft: SessionGuardrailResult;
  index: number;
  isDelivered: boolean;
  pickedUpTargetQuestion: boolean;
}) => {
  const failed = draftFailed(draft);
  const reason = failureReason(draft);
  const passed = (draft.guardrail_trees ?? []).filter(
    (tree) => guardrailPassed(tree)
  ).length;
  const total = draft.guardrail_trees?.length ?? 0;

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-brand-border">
      <div className="flex items-center gap-2 border-b border-gray-100 bg-brand-background px-3 py-2">
        <BlockLabel>Draft {index + 1}</BlockLabel>
        {isDelivered && pickedUpTargetQuestion && (
          <span className="text-[11px] text-brand-muted">
            picked up the question we asked for
          </span>
        )}
        <span className="flex-1" />
        {failed ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-red-600">
            <X className="h-3 w-3 stroke-[2.5]" aria-hidden={true} />
            Never sent
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-green-600">
            <Check className="h-3 w-3 stroke-[2.5]" aria-hidden={true} />
            {isDelivered ? "Delivered" : "Passed"} &middot; {passed} of {total}{" "}
            passed
          </span>
        )}
      </div>

      <div
        className={cn(
          "px-3 py-2.5 text-[13px] leading-relaxed",
          failed
            ? "text-brand-muted line-through decoration-red-500/55"
            : "text-brand-foreground"
        )}
      >
        {draft.message?.content}
      </div>

      {reason && (
        <div className="flex items-start gap-2 border-t border-gray-100 bg-unlikely/[0.04] px-3 py-2.5">
          <Shield failed={true} />
          <p className="text-xs leading-relaxed text-brand-muted">
            <span className="font-semibold text-brand-foreground">
              {reason.rule}.
            </span>{" "}
            {reason.criterion && (
              <>
                Criterion{" "}
                <span className="font-medium text-brand-foreground">
                  {reason.criterion}
                </span>
                {reason.value && <> evaluated {reason.value}</>}.{" "}
              </>
            )}
            {reason.reasoning}
            {reason.reasoning && " "}
            Sent back to be rewritten.
          </p>
        </div>
      )}
    </div>
  );
};

interface ConversationTurnProps {
  turn: AuditTurn;
  previousTurn?: AuditTurn;
  conversations: ConversationPair[];
  allNodes: Map<string, ChangedNode>;
  isExpanded: boolean;
  onToggle: () => void;
}

export const ConversationTurn = ({
  turn,
  previousTurn,
  conversations,
  allNodes,
  isExpanded,
  onToggle,
}: ConversationTurnProps) => {
  const drafts = draftsForTurn(turn);
  const briefing = buildBriefing(turn.state, conversations, allNodes);
  const ruledOut = outcomesRuledOutOnTurn(turn, previousTurn);
  const checks = checksRunOnTurn(turn);
  const stopped = drafts.filter(draftFailed).length;
  const deliveredIndex = drafts.length - 1;
  const targetQuestion = turn.reply?.targetQuestions?.[0];

  const summary = [
    turn.durationMs !== undefined ? formatDuration(turn.durationMs) : null,
    ruledOut > 0
      ? `${ruledOut} ${ruledOut === 1 ? "outcome" : "outcomes"} ruled out`
      : null,
    stopped > 0
      ? `${stopped} ${stopped === 1 ? "reply" : "replies"} stopped`
      : null,
    ruledOut === 0 && stopped === 0 ? "nothing decided" : null,
    checks > 0 ? `${checks} checks run` : null,
  ]
    .filter(Boolean)
    .join(" \u00b7 ");

  const guardrailNames = Array.from(
    new Set(
      (drafts.at(-1)?.guardrail_trees ?? []).map(
        (tree) => tree.guardrail_tree.name
      )
    )
  );

  return (
    <section
      className={cn(
        "flex flex-none flex-col overflow-hidden rounded-lg border",
        isExpanded ? "border-unlikely/30" : "border-brand-border"
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className={cn(
          "flex items-center gap-3 px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-unlikely/40",
          isExpanded
            ? "border-b border-brand-border bg-unlikely/[0.04]"
            : "hover:bg-brand-primary/5"
        )}
      >
        {isExpanded ? (
          <ChevronDown
            className="h-[15px] w-[15px] flex-none text-unlikely"
            aria-hidden={true}
          />
        ) : (
          <ChevronRight
            className="h-[15px] w-[15px] flex-none text-brand-muted"
            aria-hidden={true}
          />
        )}
        <span
          className={cn(
            "flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-semibold text-white",
            isExpanded ? "bg-unlikely" : "bg-brand-muted"
          )}
        >
          {turn.number}
        </span>
        <span
          className={cn(
            "min-w-0 truncate text-[13px]",
            isExpanded ? "font-semibold" : "text-brand-muted"
          )}
        >
          &ldquo;{turn.customerMessage}&rdquo;
        </span>
        <span className="flex-1" />
        <span className="flex-none text-xs text-brand-muted">{summary}</span>
        {drafts.length > 0 && <ShieldSequence drafts={drafts} />}
      </button>

      {isExpanded && (
        <div className="flex min-h-0 gap-4 p-4">
          <div className="flex w-[520px] flex-none flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <UnlikelyMark size={0.875} />
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-unlikely">
                What UnlikelyAI decided, and told the agent
              </h3>
            </div>

            <div className="flex flex-col gap-2.5 rounded-lg border border-brand-border px-3.5 py-3">
              {briefing.askNext && (
                <div className="flex flex-col gap-1.5">
                  <BlockLabel>Ask this next</BlockLabel>
                  <p className="text-[13px] leading-normal">
                    &ldquo;{briefing.askNext.question}&rdquo;
                    {briefing.askNext.waitingOn.length > 0 && (
                      <>
                        {" "}
                        {briefing.askNext.waitingOn.join(" and ")}{" "}
                        {briefing.askNext.waitingOn.length === 1
                          ? "is"
                          : "are both"}{" "}
                        waiting on it.
                      </>
                    )}
                  </p>
                </div>
              )}

              {briefing.contradictions.map((contradiction) => (
                <div
                  key={contradiction.question}
                  className="flex flex-col gap-1.5 border-t border-gray-100 pt-2.5 first:border-0 first:pt-0"
                >
                  <BlockLabel tone="amber">
                    <AlertTriangle className="h-3 w-3" aria-hidden={true} />
                    Contradiction
                  </BlockLabel>
                  <p className="text-[13px] leading-normal">
                    {contradictionSentence(contradiction)}
                  </p>
                </div>
              ))}

              <div className="flex gap-3.5 border-t border-gray-100 pt-2.5">
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <BlockLabel tone="green">May now raise</BlockLabel>
                  <p className="text-[13px] font-medium">
                    {briefing.mayRaise.length === 0
                      ? "Nothing yet"
                      : briefing.mayRaise
                          .map((outcome) => outcome.name)
                          .join(", ")}
                  </p>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <BlockLabel tone="red">Must not raise</BlockLabel>
                  <p className="text-[13px] leading-snug text-brand-muted">
                    {briefing.mustNotRaise.length === 0
                      ? "Nothing ruled out"
                      : briefing.mustNotRaise
                          .map((outcome) => outcome.name)
                          .join(", ")}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 rounded-lg border border-brand-border px-3.5 py-2.5">
              <BlockLabel>Recorded for replay</BlockLabel>
              <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-brand-muted">
                <span className="text-unlikely">target_question</span>:{" "}
                {JSON.stringify(targetQuestion ?? null)}
                {"\n"}
                <span className="text-unlikely">contradictions</span>:{" "}
                {JSON.stringify(
                  briefing.contradictions.map(
                    (contradiction) => contradiction.question
                  )
                )}
                {"\n"}
                <span className="text-unlikely">may_raise</span>:{" "}
                {JSON.stringify(
                  briefing.mayRaise.map((outcome) => outcome.name)
                )}
                {"\n"}
                <span className="text-unlikely">must_not_raise</span>:{" "}
                {JSON.stringify(
                  briefing.mustNotRaise.map((outcome) => outcome.name)
                )}
              </pre>
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <span
                className="h-2 w-2 flex-none rounded-sm bg-brand-primary"
                aria-hidden={true}
              />
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-brand-muted">
                What the agent wrote, and what we did with it
              </h3>
            </div>

            {drafts.length > 0 ? (
              drafts.map((draft, index) => (
                <Draft
                  key={draft.id}
                  draft={draft}
                  index={index}
                  isDelivered={index === deliveredIndex}
                  pickedUpTargetQuestion={Boolean(
                    targetQuestion && drafts.length > 1
                  )}
                />
              ))
            ) : turn.reply?.systemResponse ? (
              <div className="rounded-lg border border-brand-border px-3 py-2.5 text-[13px] leading-relaxed">
                <MarkdownContent content={turn.reply.systemResponse} />
              </div>
            ) : (
              <p className="text-[13px] text-brand-muted">
                No reply was recorded for this turn.
              </p>
            )}

            {guardrailNames.length > 0 && (
              <div className="flex items-center gap-2.5 rounded-lg border border-brand-border px-3.5 py-2.5">
                <BlockLabel>Reply guardrails run</BlockLabel>
                <span className="flex-1" />
                <span className="flex flex-wrap justify-end gap-1.5">
                  {guardrailNames.map((guardrailName) => (
                    <span
                      key={guardrailName}
                      className="rounded-full border border-brand-border bg-brand-background px-2 py-0.5 text-[11px] text-brand-muted"
                    >
                      {guardrailName}
                    </span>
                  ))}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};
